import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import { buttonBaseClasses, buttonClasses, SxProps, Theme } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import {
  deleteV1CompaniesCompanyIdTicketsTicketIdImage,
  TicketState,
} from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import { cloneElement, useContext, useMemo, useRef } from 'react';

import { Chip } from '~components/Chip/Chip';
import Menu, { MenuHandler } from '~components/Menu/Menu';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX } from '~constants/filters';
import useAiReviewDismissal from '~hooks/useAiReview/useAiReviewDismissal';
import { trackLoadsApproved } from '~hooks/useAnalytics/trackApprovals';
import useEffectiveCompanyId from '~hooks/useEffectiveCompanyId/useEffectiveCompanyId';
import { useJob } from '~hooks/useJob/useJob';
import { Load } from '~hooks/useLoad/models';
import { Ticket } from '~hooks/useTickets/models';
import LoadReviewIndicator from '~pages/Approvals/ApprovalsComponents/LoadReviewIndicator';
import ApprovalActionsMenu from '~pages/Approvals/DriverDay/components/ApprovalActionsMenu';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { getApprovalStatus } from '~utils/jobs/job-utils';
import { canEditTicket } from '~utils/tickets/ticket-utils';
import { downloadFile } from '~utils/utilFunctions';

import LoadsQueryContext from '../../JobsTable/ExpandedBlock/LoadsQueryContext';

interface ActionsColumnProps {
  load: Load;
  sx?: SxProps<Theme>;
  ticket?: Ticket;
  isOrderEditable: boolean;
}

export default function ActionsColumn({
  load,
  sx,
  ticket,
  isOrderEditable,
}: ActionsColumnProps) {
  const loadsQuery = useContext(LoadsQueryContext);
  const menuHandler = useRef<MenuHandler>(null);
  const { userStore } = useStores();
  const companyId = useEffectiveCompanyId();

  const { isApproved, isApprovable, isUnapprovable } = getApprovalStatus(
    load,
    userStore.getPermissions(),
    isOrderEditable,
  );

  const { dismiss: handleDismissAiReview, undismiss: handleUndoAiReviewDismissal } =
    useAiReviewDismissal({ companyId, load, onChanged: loadsQuery.refreshLoads });

  const dropdownMenuActions = useMemo(() => {
    const { id, imageUrl, state } = ticket ?? {};

    const hasIdAndImageUrl = id && imageUrl;
    const canDeleteTicketImage = state && canEditTicket(state as TicketState);

    return [
      {
        icon: <FileDownloadOutlined />,
        label: t('approvals.files.download_image_cta'),
        isDisabled: !hasIdAndImageUrl,
        onClick: () => {
          if (hasIdAndImageUrl) {
            downloadFile(id, imageUrl, true);
          }
        },
      },
      {
        icon: <DeleteOutlined />,
        label: t('approvals.files.delete_image_cta'),
        isDisabled: hasIdAndImageUrl ? !canDeleteTicketImage : true,
        onClick: async () => {
          if (hasIdAndImageUrl) {
            try {
              await deleteV1CompaniesCompanyIdTicketsTicketIdImage({
                path: { 'company-id': companyId, 'ticket-id': id },
              });
            } catch (error) {
              // A 404 means the image is already gone, so the delete's desired end
              // state is already true — fall through to refresh rather than
              // surfacing a noisy error (HORIZON-FE-2GQ / TRE-15571).
              const imageAlreadyDeleted =
                isAxiosError(error) && error.response?.status === 404;

              if (!imageAlreadyDeleted) {
                // The menu invokes this handler without awaiting it, so let
                // handleRequestError surface the toast + Sentry report but
                // contain its re-throw here to avoid an unhandled rejection.
                try {
                  connection.handleRequestError(
                    error,
                    t('error_messages.tickets.failed_to_delete_ticket_image'),
                  );
                } catch {
                  // Error already surfaced by handleRequestError.
                }

                return;
              }
            }

            loadsQuery.refreshTicketById(id);
          }
        },
      },
    ];
  }, [ticket]);

  return (
    <Box sx={sx}>
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}
      >
        <ApprovalActions
          companyId={companyId}
          isApprovable={isApprovable}
          isApproved={isApproved}
          isUnapprovable={isUnapprovable}
          load={load}
        />

        <LoadReviewIndicator
          aiReview={load.aiReview}
          canDismiss={isApprovable || isApproved}
          isTicketFlagged={ticket?.flagged || false}
          onDismiss={handleDismissAiReview}
          onUndoDismiss={handleUndoAiReviewDismissal}
        />

        <Menu
          ref={menuHandler}
          menuTrigger={
            <SmallButton
              color="brandV2TreadGray3"
              variant="text"
              startIcon={<MoreHoriz />}
              sx={(theme) => ({
                [`&.${buttonBaseClasses.root}`]: {
                  border: 'solid 1px transparent',
                  borderRadius: '50%',
                  height: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
                  minWidth: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
                  p: 0,
                  width: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
                  '&:hover': { borderColor: theme.brandV2.colors.treadGray3 },
                  [`.${buttonClasses.startIcon}`]: { mx: 0 },
                },
              })}
            />
          }
        >
          {dropdownMenuActions.map((action, index) => {
            const menuItems = [
              <MenuItem
                key={action.label}
                disabled={action.isDisabled}
                onClick={(event) => {
                  event.stopPropagation();
                  action.onClick();
                  menuHandler.current?.onClose?.();
                }}
              >
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    width: '100%',
                    gap: 1,
                  }}
                >
                  {cloneElement(action.icon, {
                    sx: { ...action.icon.props.sx, fontSize: '16px' },
                  })}

                  <Typography
                    sx={(theme) => ({
                      color: theme.brandV2.colors.treadBlack,
                      fontSize: '12px',
                    })}
                  >
                    {action.label}
                  </Typography>
                </Box>
              </MenuItem>,
            ];

            const isLastAction = index === dropdownMenuActions.length - 1;
            const hasAtLeastTwoAvailableActions = dropdownMenuActions.length >= 2;
            const shouldShowDivider = isLastAction && hasAtLeastTwoAvailableActions;

            if (shouldShowDivider) {
              menuItems.unshift(<Divider key={`${action.label}-divider`} />);
            }

            return menuItems;
          })}
        </Menu>
      </Box>
    </Box>
  );
}

interface ApprovalActionsProps {
  companyId: string;
  isApprovable: boolean;
  isApproved: boolean;
  isUnapprovable: boolean;
  load: Load;
  onApprovalChanged?: () => void;
}

export function ApprovalActions({
  companyId,
  isApprovable: isLoadApprovable,
  isApproved: isLoadApproved,
  isUnapprovable: isLoadUnapprovable,
  load,
  onApprovalChanged,
}: ApprovalActionsProps) {
  const loadsQuery = useContext(LoadsQueryContext);
  const { approveLoad, unapproveLoad, isUpdating } = useJob();

  if (isLoadApproved && isLoadUnapprovable) {
    return (
      <ApprovalActionsMenu
        approved={isLoadApproved}
        isLoading={isUpdating}
        onMenuItemClick={() => {
          unapproveLoad(load.id, companyId).then(() => {
            loadsQuery.refreshLoads();
            onApprovalChanged?.();
          });
        }}
      />
    );
  }

  if (isLoadApprovable) {
    return (
      <Button
        color="secondary"
        disabled={isUpdating}
        variant="outlined"
        onClick={(event) => {
          event.stopPropagation();
          approveLoad(load.id, companyId).then(() => {
            trackLoadsApproved([load]);
            loadsQuery.refreshLoads();
            onApprovalChanged?.();
          });
        }}
        sx={{ fontSize: '12px', minWidth: 0, px: '4px', py: 0, height: '24px' }}
      >
        {t('approvals.approve')}
      </Button>
    );
  }

  return (
    <Chip
      color={isLoadApproved ? 'success' : 'secondary'}
      label={isLoadApproved ? t('status.approved') : t('status.pending')}
      hideDropdownArrow
    />
  );
}
